import React, { useState, useRef, useEffect } from 'react';
import commonStyles from '../../DataDashboard.module.scss';
import styles from './SchoolStats.module.scss';
import { navigate } from '@reach/router';
import schoolPNG from '../../../../images/school.png';
import { useQuery } from '@apollo/react-hooks';
import {
    SCHOOL_MONTHLY_REPORT_DATA,
    GET_SCHOOL_BY_ID,
    ACADEMIC_MONTHLY_REPORTS
} from '../../services/graphql';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { monthNames, radiusOpts, lineColors, academicMonthOrder } from '../../utils/chart-helpers';
import BackLink from '../../../../components/BackLink';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { useLazyQuery } from '@apollo/react-hooks';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const SchoolStats = ({ schoolId }) => {
    const [selectedData, setSelectedData] = useState([]);
    const [readingLogs, setReadingLogs] = useState(null)
    const [booksCompleted, setBooksCompleted] = useState(null);
    const [bookTypes, setBookTypes] = useState(null);
    const [loggedBy, setLoggedBy] = useState(null);
    const [academicYear, setAcademicYear] = useState(moment().toDate().getMonth() >= 8 ? moment().year() + 1 : moment().year());

    const readingLogsChartRef = useRef(null);
    const booksCompletedChartRef = useRef(null);

    let labels = [];

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                // ticks: {
                //     stepSize: 1
                // }
            },
            x: {
                offset: selectedData.length === 0 ? true : false,
            },
        },
    };

    const getMaxDate = () => {
        const now = new Date();
        const year = now.getMonth() >= 8 ? now.getFullYear() + 1 : now.getFullYear();
        return new Date(year, 0, 1);
    };

    const handleAcademicYearChange = (date) => {
        const selectedAcademicYear = date.getFullYear();
        setAcademicYear(selectedAcademicYear);
        getSchoolByCode({
            variables: {
                schoolCode: schoolData?.school.schoolCode,
                academicYear: selectedAcademicYear,
            }
        });
    };

    const {
        data: schoolData,
        loading: schoolDataLoading,
        error: schoolDataError,
    } = useQuery(GET_SCHOOL_BY_ID, {
        variables: {
            schoolId,
        },
        onCompleted: (data) => {
            if (data.school.schoolCode) {
                getSchoolByCode({
                    variables: {
                        schoolCode: schoolData?.school.schoolCode,
                        academicYear: academicYear,
                    }
                })
            }
        }
    });

    const [loadAverageAcademicMonthlyReports, { data: averageAcademicMonthlyReports, error: averageAcademicMonthlyReportsError, loading: averageAcademicMonthlyReportsLoading }] = useLazyQuery(ACADEMIC_MONTHLY_REPORTS, {
        fetchPolicy: 'network-only'
    });

    const [getSchoolByCode, { data: schoolStatsData, loading: schoolStatsDataLoading, error: schoolStatsDataError, }] = useLazyQuery(SCHOOL_MONTHLY_REPORT_DATA, {
        fetchPolicy: 'network-only',
        variables: {
            schoolCode: schoolData?.school.schoolCode,
            academicYear: academicYear,
        },
        onCompleted: () => {
            loadAverageAcademicMonthlyReports({
                variables: {
                    academicYear: academicYear
                }
            })
        }
    });

    useEffect(() => {
        if (schoolData && schoolStatsData) {
            const monthlyReportData = schoolStatsData.schoolByCode.monthlyReportData;
            setSelectedData(monthlyReportData);
            const monthNumbers = monthlyReportData.map(mr => mr.month);
            labels = [...new Set(monthNumbers.map(month => monthNames[month - 1]))];

            setReadingLogs({
                labels,
                datasets: [
                    {
                        label: `Reading Logs ${academicYear}`,
                        data: computeDataLogs(monthlyReportData, ['totalLogs'], labels),
                        ...lineColors[0],
                        ...radiusOpts
                    },
                ],
            });

            setBooksCompleted({
                labels,
                datasets: [
                    {
                        label: 'Books Completed',
                        data: computeDataLogs(monthlyReportData, ['totalBooksCompleted'], labels),
                        ...lineColors[0],
                        ...radiusOpts
                    },
                ],
            })

            setLoggedBy({
                labels,
                datasets: [
                    {
                        label: 'Parent Logs',
                        data: computeDataLogs(monthlyReportData, ['parentLogs'], labels),
                        ...lineColors[0],
                        ...radiusOpts
                    },
                    {
                        label: 'Student Logs',
                        data: computeDataLogs(monthlyReportData, ['studentLogs'], labels),
                        ...lineColors[1],
                        ...radiusOpts
                    },
                    {
                        label: 'Teacher Logs',
                        data: computeDataLogs(monthlyReportData, ['teacherLogs'], labels),
                        ...lineColors[2],
                        ...radiusOpts
                    },
                ],
            })

            setBookTypes({
                labels,
                datasets: [
                    {
                        label: 'Group Books',
                        data: computeDataLogs(monthlyReportData, ['groupBooksCompleted'], labels),
                        ...lineColors[0],
                        ...radiusOpts
                    },

                    {
                        label: 'Home Books',
                        data: computeDataLogs(monthlyReportData, ['homeBooksCompleted'], labels),
                        ...lineColors[1],
                        ...radiusOpts
                    },
                    {
                        label: 'Library Books',
                        data: computeDataLogs(monthlyReportData, ['libraryBooksCompleted'], labels),
                        ...lineColors[2],
                        ...radiusOpts
                    },
                    {
                        label: 'School Books',
                        data: computeDataLogs(monthlyReportData, ['schoolBooksCompleted'], labels),
                        ...lineColors[3],
                        ...radiusOpts
                    },
                ],
            })
        }
    }, [schoolStatsData]);


    useEffect(() => {
        if (averageAcademicMonthlyReports && averageAcademicMonthlyReports.academicMonthlyReports) {
            const monthlyReportData = averageAcademicMonthlyReports.academicMonthlyReports;
            const monthNumbers = monthlyReportData.map(mr => mr.month);
            labels = [...new Set(monthNumbers.map(month => monthNames[month - 1]))];

            const averageReadingLogsData = {
                label: 'BoomReader National Average',
                data: computeDataLogs(monthlyReportData, ['avgParentLogs', 'avgStudentLogs', 'avgTeacherLogs'], labels, true),
                ...lineColors[2],
                ...radiusOpts
            };

            // Merge months as chart labels from current / prev year
            const readingLabelsMerged = [...new Set([...readingLogs?.labels, ...labels])];
            // Sort merged months as chart labels based on year type (calendar / academic)
            const readingLabelsSorterd = readingLabelsMerged.sort(
                (a, b) => academicMonthOrder[a] - academicMonthOrder[b]
            );

            // Since data the dataseet from the prev year is already maped to the corresponding months, we need to adjust that dataset of the current year with respect to the merged labels
            const readingLogsDatasetsAfterMerge = {
                label: readingLogs.datasets[0].label,
                // We basically go through each month and check if the dataset of the current year has data for that month. If not, return 0, otherwise return the data.
                data: readingLabelsSorterd.map((readingLabel) => readingLogs.labels.indexOf(readingLabel) === -1 ? 0 : readingLogs.datasets[0].data[readingLogs.labels.indexOf(readingLabel)]),
                ...lineColors[0],
                ...radiusOpts
            };


            const averageBooksCompleted = {
                label: 'BoomReader National Average',
                data: computeDataLogs(monthlyReportData, ['avgHomeBooks', 'avgLibraryBooks', 'avgSchoolBooks'], labels, true),
                ...lineColors[2],
                ...radiusOpts
            };

            // Merge months as chart labels from current / prev year
            const booksCompletedLabelsMerged = [...new Set([...booksCompleted?.labels, ...labels])];
            // Sort merged months as chart labels based on year type (calendar / academic)
            const booksCompletedLabelsSorterd = booksCompletedLabelsMerged.sort(
                (a, b) => academicMonthOrder[a] - academicMonthOrder[b]
            );
            // Since data the dataseet from the prev year is already maped to the corresponding months, we need to adjust that dataset of the current year with respect to the merged labels
            const booksCompletedDatasetsAfterMerge = {
                label: booksCompleted.datasets[0].label,
                // We basically go through each month and check if the dataset of the current year has data for that month. If not, return 0, otherwise return the data.
                data: booksCompletedLabelsSorterd.map((bookCompletedlabel) => booksCompleted.labels.indexOf(bookCompletedlabel) === -1 ? 0 : booksCompleted.datasets[0].data[booksCompleted.labels.indexOf(bookCompletedlabel)]),
                ...lineColors[0],
                ...radiusOpts
            };

            // Update data for readingLogs / booksCompleted to contain the data from both current / prev year.
            setReadingLogs({
                labels: readingLabelsSorterd,
                datasets: [readingLogsDatasetsAfterMerge, averageReadingLogsData]
            });
            setBooksCompleted({
                labels: booksCompletedLabelsSorterd,
                datasets: [booksCompletedDatasetsAfterMerge, averageBooksCompleted]
            });

            // Update charts
            readingLogsChartRef.current.update();
            booksCompletedChartRef.current.update();

        }
    }, [averageAcademicMonthlyReports])

    const computeDataLogs = (graphData, keys, lables, isAverage = false) => {
        const dataForLabels = [];
        lables.forEach((label, index) => {
            const labelIndex = monthNames.indexOf(label);
            const dataForLabelIndex = graphData.filter((item) => item.month - 1 === labelIndex);
            if (dataForLabelIndex) {
                dataForLabels.push(dataForLabelIndex.reduce((acc, curr) => acc + computeKeysSumOfItem(curr, keys), 0))
            }

            if (isAverage) {
                const numberOfStudentsInSchool = schoolData.school.students.length;
                dataForLabels[index] = dataForLabels[index] * numberOfStudentsInSchool;
            }
        });
        return dataForLabels;
    };

    const computeKeysSumOfItem = (monthData, keys) => {
        let sum = 0;
        keys.forEach((key) => {
            sum += monthData[key];
        })
        return sum;
    };

    if (schoolDataLoading) {
        return <p>Loading...</p>;
    }

    if (schoolDataError) {
        return <p>{schoolDataError.error}</p>;
    }

    return (
        <>
            <BackLink to='/dataDashboard/schoolSearch' text='Back' />

            <div className={commonStyles.dataDashboardHeader}>
                <div>
                    <h2
                        style={{ marginBottom: '10px', cursor: 'pointer' }}
                        onClick={() =>
                            navigate(
                                `/dataDashboard/schoolStats/schoolHome/${schoolId}`,
                            )
                        }
                    >
                        {schoolData.school.name}
                        <span className='body-text u-m-left-2'>
                            {schoolData.school.schoolCode}
                        </span>
                    </h2>
                    <h4 style={{ marginBottom: '10px' }}>
                        {schoolData.school.address} {schoolData.school.address2}{' '}
                        {schoolData.school.city} &dash; {schoolData.school.zip}
                    </h4>
                    <h4>
                        <p>
                            Contact: {schoolData.school.admins[0]?.displayName}{' '}
                            {schoolData.school.admins[0]?.email}{' '}
                            {schoolData.school.schoolPhoneNumber}
                        </p>
                    </h4>
                </div>

                <div className={styles.schoolLogo}>
                    <img
                        src={schoolData.school.avatarUrl ? schoolData.school.avatarUrl : schoolPNG}
                        alt='school-image'
                        width='79'
                        height='71'
                    ></img>
                </div>
            </div>

            <div className={styles.yearSelector}>
                <h3>Academic</h3>
                <DatePicker
                    selected={new Date(academicYear, 0, 1)}
                    showYearPicker
                    dateFormat="yyyy"
                    maxDate={getMaxDate()}
                    onChange={handleAcademicYearChange}
                />
            </div>

            {readingLogs && (<div>
                <div className={styles.ProgressWrapper}>
                    <span className={styles.Title}>Reading logs</span>
                    <div className={styles.Timeline}>
                        <div className={styles.Progress}></div>
                    </div>
                </div>

                <Line
                    ref={readingLogsChartRef}
                    options={options}
                    data={readingLogs}
                />
            </div>)}

            {booksCompleted && (<div>
                <div className={styles.ProgressWrapper}>
                    <span className={styles.Title}>Books Completed</span>
                    <div className={styles.Timeline}>
                        <div className={styles.Progress}></div>
                    </div>
                </div>

                <Line
                    ref={booksCompletedChartRef}
                    options={options}
                    data={booksCompleted}
                />
            </div>)}

            {bookTypes && (<div>
                <div className={styles.ProgressWrapper}>
                    <span className={styles.Title}>Book Types</span>
                    <div className={styles.Timeline}>
                        <div className={styles.Progress}></div>
                    </div>
                </div>

                <Line
                    options={options}
                    data={bookTypes}
                />
            </div>)}

            {loggedBy && (<div>
                <div className={styles.ProgressWrapper}>
                    <span className={styles.Title}>Logged By</span>
                    <div className={styles.Timeline}>
                        <div className={styles.Progress}></div>
                    </div>
                </div>
                <Line
                    options={options}
                    data={loggedBy}
                />
            </div>)}

        </>
    );
};

export default SchoolStats;
